import {createFeature, createReducer, on} from "@ngrx/store";
import {APP_CONSTANTS} from "../../app.constants";
import {ConsolidatedUserModel, FilterInterface, PaginationInfoInterface, UserModel} from "../../interfaces";
import * as UserActions from "../actions/users.actions";

export const usersFeatureKey = "Users";

export interface State {
  userItems: Array<UserModel>;
  userItemsPaginated: Array<UserModel>;
  consolidatedUsers: Array<ConsolidatedUserModel>;
  userFilters: FilterInterface;
  userPaginationInfo: PaginationInfoInterface;
}

export const initialState: State = {
  userItems: [],
  userItemsPaginated: [],
  consolidatedUsers: [],
  userFilters: {
    limit: APP_CONSTANTS.PAGINATION_PAGE_SIZE,
    offset: 0
  },
  userPaginationInfo: {
    from: 0,
    to: 0,
    totalResults: 0,
    currentPage: 0,
    totalPages: 1
  }
};

export const usersFeature = createFeature({
  name: usersFeatureKey,
  reducer: createReducer(
    initialState,
    on(
      UserActions.addUser,
      (state: State, {user}): State => ({
        ...state,
        userItems: [...state.userItems, user]
      })
    ),
    on(
      UserActions.addUsers,
      (state: State, {users}): State => ({
        ...state,
        userItems: [...state.userItems, ...users]
      })
    ),
    on(UserActions.updateUser, (state: State, {user}): State => {
      const users = state.userItems.map(item => (item.id === user.id ? user : item));
      return {
        ...state,
        userItems: users
      };
    }),
    on(
      UserActions.setUsers,
      (state: State, {users}): State => ({
        ...state,
        userItems: users
      })
    ),
    on(
      UserActions.setPaginatedUsers,
      (state: State, {users}): State => ({
        ...state,
        userItemsPaginated: users
      })
    ),
    on(
      UserActions.setConsolidatedUsers,
      (state: State, {users}): State => ({
        ...state,
        consolidatedUsers: users
      })
    ),
    on(
      UserActions.setUsersPaginationInfo,
      (state: State, {paginationInfo}): State => ({...state, userPaginationInfo: paginationInfo})
    ),
    on(
      UserActions.gotoUsersPage,
      (state: State, {page}): State => ({
        ...state,
        userFilters: {...state.userFilters, offset: state.userFilters.limit * (page - 1)},
        userPaginationInfo: {...state.userPaginationInfo, currentPage: page}
      })
    ),
    on(UserActions.applyUsersFilters, (state: State, {filters}): State => {
      const newFilters = {...state.userFilters};
      filters.forEach(({key, value}) => {
        newFilters[key as string] = value;
      });
      newFilters.offset = 0;
      return {...state, userFilters: newFilters};
    }),
    on(UserActions.resetUsersFilters, (state: State): State => ({...state, userFilters: initialState.userFilters}))
  )
});

export const {
  name,
  reducer,
  selectUserItems,
  selectUserItemsPaginated,
  selectConsolidatedUsers,
  selectUserFilters,
  selectUserPaginationInfo
} = usersFeature;
